"use client";

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../../components/navbar";
import Footer from "../../../../../components/footer";
import Button from "../../../../../components/Button";
import Checkbox from "../../../../../components/Checkbox";
import { Check } from "lucide-react";

import { useConsultation } from "../../../../../context/ConsultationContext";
import { validateAdultVaccinationConsentForm, validateIdOrDob } from "../../utils/validation";
import SectionCard from "./components/SectionCard";
import TextInputField from "./components/TextInputField";
import TextAreaField from "./components/TextAreaField";
import FormInput from "../../../../../components/FormInput";

import styles from "./css/InjectionAdministration.module.css";

const initialForm = { patientFullName: "", patientIdOrDob: "", patientContactNumber: "", dateOfVisit: "", hasNoKnownAllergies: false, hasKnownAllergies: false, knownAllergiesDetails: "", hadAllergicReactionToVaccine: false, allergicReactionDetails: "", vaccineName: "", batchNumber: "", expiryDate: "", popiaConsent: false, clinicalFollowUpConsent: false, marketingConsent: false, patientSignature: "", patientSignatureDate: "", administratorName: "", administratorTitleRank: "", administratorSignatureDate: "" };

export default function AdultVacination() {
  const navigate = useNavigate();
  const location = useLocation();
  const { consultationData, setIsInConsultation, getStepDraft, setStepDraft } = useConsultation();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const todayStr = useMemo(() => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(today.getDate())}-${pad(today.getMonth() + 1)}-${today.getFullYear()}`;
  }, [today]);

  const patient = consultationData?.patient || {};
  const mainMember = consultationData?.patient?.mainMember || {};

  const patientName = useMemo(() => {
    const first = String(patient?.firstname || "").trim();
    const last = String(patient?.surname || "").trim();
    const full = `${first} ${last}`.trim();
    return full;
  }, [patient?.firstname, patient?.surname]);

  const mainMemberName = useMemo(() => {
    const first = String(mainMember?.firstname || "").trim();
    const last = String(mainMember?.surname || "").trim();
    const full = `${first} ${last}`.trim();
    return full;
  }, [mainMember?.firstname, mainMember?.surname]);

  const [formData, setFormData] = useState(() => ({
    ...initialForm,
    patientFullName: mainMemberName || patientName || initialForm.patientFullName,
    patientSignatureDate: todayStr,
    administratorSignatureDate: todayStr,
  }));

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "patientIdOrDob") {
      const res = validateIdOrDob(value);
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        if (res.isValid) {
          delete newErrors.patientIdOrDob;
        } else {
          newErrors.patientIdOrDob = res.error;
        }
        return newErrors;
      });
      return;
    }

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const focusFirstError = (errors) => {
    const fieldOrder = [
      "patientFullName", "patientIdOrDob", "patientContactNumber","dateOfVisit","vaccineName","batchNumber", "expiryDate","popiaConsent","patientSignature","administratorName",
    ];

    const field = fieldOrder.find((f) => errors?.[f]);
    if (!field) return;

    const el = document.getElementById(field) || document.querySelector(`[name="${field}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (el?.focus) el.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    // Validate form
    const errors = validateAdultVaccinationConsentForm(formData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setTimeout(() => focusFirstError(errors), 0);
      return;
    }

    setSaveError("");
    setSaving(true);
    try {
      const payload = {
        ...formData,
        appointmentId: consultationData?.appointmentId ?? null,
        patientId: consultationData?.patientId ?? null,
      };

      // Save to consultation draft context (for DB save on Invoice Finalize)
      const existing = getStepDraft?.("consentForms", {}) || {};
      setStepDraft?.("consentForms", {
        ...existing,
        adult_vaccination: {
          answers: payload,
          patientSigned: !!formData.patientSignature,
          patientSignedAt: formData.patientSignatureDate ?? null,
          practitionerSigned: !!formData.administratorName,
          practitionerSignedAt: formData.administratorSignatureDate ?? null,
        },
      });

      try {
        sessionStorage.setItem("clinic.consent.adultVaccination", JSON.stringify(payload));
      } catch {}

      const continueTo = location?.state?.continueTo;
      setIsInConsultation?.(true);
      navigate(typeof continueTo === "string" && continueTo.trim() ? continueTo : "/customer/clinic/patient-overview");
    } catch (err) {
      setSaveError(err?.message || "Failed to save consent form.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <form className={styles.formContainer} onSubmit={handleSubmit}>
            <header className={styles.docHeader}>
              <div className={styles.docHeaderTop}>
                <div className={styles.pharmacyName}>Durban North Pharmacy</div>
                <div className={styles.pharmacyContact}>35 Umhlanga Rocks Drive | Tel: 031 563 7577</div>
              </div>
              <div className={styles.purpleRule} />
              <h1 className={styles.docTitle}>Consent Form — Vaccination Adult (18 years and older)</h1>
            </header>

            <SectionCard title="1. Patient Details" styles={styles}>
              <div className={styles.grid2}>
                <TextInputField
                  id="patientFullName"
                  name="patientFullName"
                  label="Full Name"
                  value={formData.patientFullName}
                  onChange={handleInputChange}
                  placeholder="Full name"
                  autoComplete="name"
                  error={validationErrors.patientFullName}
                  styles={styles}
                />

                <TextInputField
                  id="patientIdOrDob"
                  name="patientIdOrDob"
                  label="ID Number / Date of Birth"
                  value={formData.patientIdOrDob}
                  onChange={handleInputChange}
                  placeholder="ID number or DOB"
                  error={validationErrors.patientIdOrDob}
                  styles={styles}
                />

                <TextInputField
                  id="patientContactNumber"
                  name="patientContactNumber"
                  label="Contact Number"
                  value={formData.patientContactNumber}
                  onChange={handleInputChange}
                  placeholder="Contact number"
                  autoComplete="tel"
                  error={validationErrors.patientContactNumber}
                  styles={styles}
                />

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Date of Visit"
                    type="text"
                    name="dateOfVisit"
                    value={formData.dateOfVisit}
                    onChange={handleInputChange}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    inputClassName={validationErrors.dateOfVisit ? styles.inputError : ""}
                  />
                  {validationErrors.dateOfVisit && <div className={styles.errorMessage}>{validationErrors.dateOfVisit}</div>}
                </div>
              </div>
            </SectionCard>

            {/* Parent/Legal Guardian Details - Only show if main member data is available */}
            {mainMember && mainMemberName && (
              <SectionCard title="Parent / Legal Guardian Details" styles={styles}>
                <div className={styles.grid2}>
                  <TextInputField
                    id="mainMemberName"
                    name="mainMemberName"
                    label="Full Name"
                    value={mainMemberName}
                    readOnly
                    styles={styles}
                  />

                  <TextInputField
                    id="mainMemberIdNumber"
                    name="mainMemberIdNumber"
                    label="ID Number"
                    value={mainMember.idnumber || ""}
                    readOnly
                    styles={styles}
                  />

                  <TextInputField
                    id="mainMemberContactNumber"
                    name="mainMemberContactNumber"
                    label="Contact Number"
                    value={mainMember.contactnumber || ""}
                    readOnly
                    styles={styles}
                  />

                  <TextInputField
                    id="mainMemberMedicalAidProvider"
                    name="mainMemberMedicalAidProvider"
                    label="Medical Aid Provider"
                    value={mainMember.medicalaidprovider || ""}
                    readOnly
                    styles={styles}
                  />

                  <TextInputField
                    id="mainMemberMedicalAidNumber"
                    name="mainMemberMedicalAidNumber"
                    label="Medical Aid Number"
                    value={mainMember.medicalaidnumber || ""}
                    readOnly
                    styles={styles}
                  />

                  <TextInputField
                    id="mainMemberAccountNumber"
                    name="mainMemberAccountNumber"
                    label="Account Number"
                    value={mainMember.accountnumber || ""}
                    readOnly
                    styles={styles}
                  />
                </div>
              </SectionCard>
            )}

            <SectionCard title="2. Allergy Declaration" styles={styles}>
              <div className={styles.checkboxRow}>
                <Checkbox
                  id="hasNoKnownAllergies"
                  label="I have NO known allergies"
                  checked={formData.hasNoKnownAllergies}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      hasNoKnownAllergies: e.target.checked,
                      hasKnownAllergies: e.target.checked ? false : p.hasKnownAllergies,
                      knownAllergiesDetails: e.target.checked ? "" : p.knownAllergiesDetails,
                    }))
                  }
                />
              </div>

              <div className={styles.checkboxRow}>
                <Checkbox
                  id="hasKnownAllergies"
                  label="I have the following known allergies (including medicines, vaccines, food, latex, preservatives, or other substances):"
                  checked={formData.hasKnownAllergies}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      hasKnownAllergies: e.target.checked,
                      hasNoKnownAllergies: e.target.checked ? false : p.hasNoKnownAllergies,
                      knownAllergiesDetails: e.target.checked ? p.knownAllergiesDetails : "",
                    }))
                  }
                />
              </div>
              {formData.hasKnownAllergies && (
                <TextAreaField
                  id="knownAllergiesDetails"
                  name="knownAllergiesDetails"
                  value={formData.knownAllergiesDetails}
                  onChange={handleInputChange}
                  placeholder="Describe known allergies"
                  styles={styles}
                />
              )}

              <div className={styles.checkboxRow}>
                <Checkbox
                  id="hadAllergicReactionToVaccine"
                  label="I have previously had an allergic reaction to a vaccine or injection. If yes, describe:"
                  checked={formData.hadAllergicReactionToVaccine}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      hadAllergicReactionToVaccine: e.target.checked,
                      allergicReactionDetails: e.target.checked ? p.allergicReactionDetails : "",
                    }))
                  }
                />
              </div>
              {formData.hadAllergicReactionToVaccine && (
                <TextAreaField
                  id="allergicReactionDetails"
                  name="allergicReactionDetails"
                  value={formData.allergicReactionDetails}
                  onChange={handleInputChange}
                  placeholder="Describe previous reaction"
                  styles={styles}
                />
              )}
            </SectionCard>

            <SectionCard title="3. Vaccine Details — For Clinic Completion" styles={styles}>
              <div className={styles.grid2}>
                <TextInputField
                  id="vaccineName"
                  name="vaccineName"
                  label="Vaccine Name"
                  value={formData.vaccineName}
                  onChange={handleInputChange}
                  placeholder="Vaccine name"
                  error={validationErrors.vaccineName}
                  styles={styles}
                />

                <TextInputField
                  id="batchNumber"
                  name="batchNumber"
                  label="Batch Number"
                  value={formData.batchNumber}
                  onChange={handleInputChange}
                  placeholder="Batch number"
                  error={validationErrors.batchNumber}
                  styles={styles}
                />

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Expiry Date"
                    type="text"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleInputChange}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    inputClassName={validationErrors.expiryDate ? styles.inputError : ""}
                  />
                  {validationErrors.expiryDate && <div className={styles.errorMessage}>{validationErrors.expiryDate}</div>}
                </div>
                <div />
              </div>
            </SectionCard>

            <SectionCard title="4. Nature of Service" styles={styles}>
              <p className={styles.statement}>
                I understand that I am receiving a vaccination administered by a Nurse/Pharmacist in accordance with approved scope of practice and
                national guidelines.
              </p>
            </SectionCard>

            <SectionCard title="5. Risks &amp; Side Effects" styles={styles}>
              <p className={styles.statement}>
                Possible side effects may include pain, redness, or swelling at the injection site, mild fever, headache, fatigue, and rare allergic
                reactions. I confirm that I have disclosed relevant medical history, allergies, pregnancy status (if applicable), and current
                medication use.
              </p>
            </SectionCard>

            <SectionCard title="6. POPIA - Use of Personal Information" styles={styles}>
              <p className={styles.statement}>
                I consent to the lawful collection, recording, and secure storage of my personal and health information for the purpose of providing
                pharmacy clinical services, in accordance with the Protection of Personal Information Act (POPIA).
              </p>

              <div className={styles.checkboxRow}>
                <Checkbox
                  id="popiaConsent"
                  label="I consent to the use of my personal information (POPIA)."
                  checked={formData.popiaConsent}
                  onChange={(e) => setFormData((p) => ({ ...p, popiaConsent: e.target.checked }))}
                />
              </div>
              {validationErrors.popiaConsent && (
                <div className={styles.errorMessage}>{validationErrors.popiaConsent}</div>
              )}
            </SectionCard>

            <SectionCard title="7. Communication Consent (Optional)" styles={styles}>
              <div className={styles.checkboxRow}>
                <Checkbox
                  id="clinicalFollowUpConsent"
                  label="Clinical follow-up consent"
                  checked={formData.clinicalFollowUpConsent}
                  onChange={(e) => setFormData((p) => ({ ...p, clinicalFollowUpConsent: e.target.checked }))}
                />
              </div>

              <div className={styles.checkboxRow}>
                <Checkbox
                  id="marketingConsent"
                  label="Marketing and health promotions (optional)"
                  checked={formData.marketingConsent}
                  onChange={(e) => setFormData((p) => ({ ...p, marketingConsent: e.target.checked }))}
                />
              </div>
            </SectionCard>

            <SectionCard title="8. Voluntary Consent" styles={styles}>
              <p className={styles.statement}>I voluntarily consent to receiving the vaccination described above.</p>
              <div className={styles.grid2}>
                <TextInputField
                  id="patientSignature"
                  name="patientSignature"
                  label="Patient Signature"
                  value={formData.patientSignature}
                  onChange={handleInputChange}
                  placeholder="Type name as signature"
                  error={validationErrors.patientSignature}
                  styles={styles}
                />
                <div className={styles.inputGroup}>
                  <FormInput
                    label="Date"
                    type="text"
                    name="patientSignatureDate"
                    value={formData.patientSignatureDate}
                    onChange={() => {}}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    disabled
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard title="9. Administrator and Pharmacy Details" styles={styles}>
              <div className={styles.grid2}>
                <TextInputField
                  id="administratorName"
                  name="administratorName"
                  label="Name of Administrator"
                  value={formData.administratorName}
                  onChange={handleInputChange}
                  placeholder="Administrator name"
                  error={validationErrors.administratorName}
                  styles={styles}
                />
                <TextInputField
                  id="administratorTitleRank"
                  name="administratorTitleRank"
                  label="Professional Title / Rank"
                  value={formData.administratorTitleRank}
                  onChange={handleInputChange}
                  placeholder="Title / rank"
                  error={validationErrors.administratorTitleRank}
                  styles={styles}
                />
                <div className={styles.inputGroup}>
                  <FormInput
                    label="Date"
                    type="text"
                    name="administratorSignatureDate"
                    value={formData.administratorSignatureDate}
                    onChange={() => {}}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    disabled
                  />
                </div>
                <div />
              </div>
            </SectionCard>

            {saveError && <div className={styles.inlineError}>{saveError}</div>}

            <div className={styles.completeContainer}>
              <Button variant="primary" className={styles.completeButton} disabled={saving} type="submit">
                {saving ? (
                  "Completing..."
                ) : (
                  <>
                    Complete <Check size={22} />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
}